/* R796 Battlegrounds page: the vendored effects libraries (contract §7.6).
   The four files sit flat in assets/ (src/vendor/vendor.json, checked by
   harness/check-vendor.cjs); their URLs arrive in window.HOH_VENDOR from Liquid.
   They load as CLASSIC scripts, because pixi-filters writes this.PIXI.filters,
   which is undefined inside a module. Scripts are injected one after another
   (async = false) in the fixed order pixi, pixiFilters, gsap, customEase, each
   after the one before it has run, so execution order is certain. A dependent
   is skipped when its base failed: pixi-filters evaluated without PIXI throws
   at the top level, which would be a page error.
   The feature flag is HOH_BG_CONFIG.vendorFx: false skips every download and
   the page stays on the 2D canvas. load() never rejects: it resolves
   {ok:true, PIXI, gsap, CustomEase} or {ok:false, reason}, and a failure sets
   ui.fx = '2d'. Injection always waits for a fresh task, so even a call from a
   tap handler never runs inside that handler (INP). Nothing preloads at boot (since 2026-09-28: no module used
   them, about 1.1 MB on phones); a module calls load() or preload() when it needs them. */

export const ORDER = Object.freeze(['pixi', 'pixiFilters', 'gsap', 'customEase']);
const NEEDS = Object.freeze({ pixiFilters: 'pixi', customEase: 'gsap' });
const GLOBAL = Object.freeze({
  pixi: () => window.PIXI && window.PIXI.Application,
  pixiFilters: () => window.PIXI && window.PIXI.filters,
  gsap: () => window.gsap,
  customEase: () => window.CustomEase
});
const TIMEOUT_MS = 15000;

const loaded = new Map();   /* name → Promise<{ok, name, reason?}> (success only kept) */
let inflight = null;
const log = [];             /* what happened, for proofs: {name, src, at, event, result} */

function config() { return (typeof window !== 'undefined' && window.HOH_BG_CONFIG) || {}; }
function urls() { return (typeof window !== 'undefined' && window.HOH_VENDOR) || {}; }

function setFx(store, fx) {
  if (!store) return;
  try {
    const ui = store.get().ui || {};
    if (ui.fx !== fx) store.set({ ui: Object.assign({}, ui, { fx: fx }) });
  } catch (_) {}
}

function withDeps(names) {
  const want = new Set();
  for (const n of names || ORDER) {
    if (ORDER.indexOf(n) < 0) continue;
    if (NEEDS[n]) want.add(NEEDS[n]);
    want.add(n);
  }
  return ORDER.filter(n => want.has(n));
}

function nextTask() {
  return new Promise(resolve => {
    try {
      const ch = new MessageChannel();
      ch.port1.onmessage = () => resolve();
      ch.port2.postMessage(0);
    } catch (_) { setTimeout(resolve, 0); }
  });
}

function inject(name) {
  if (loaded.has(name)) return loaded.get(name);
  const src = urls()[name];
  const entry = { name: name, src: src || null, at: 0, event: null, result: null };
  log.push(entry);
  const p = new Promise(resolve => {
    if (!src || typeof src !== 'string') { entry.result = 'unconfigured'; resolve({ ok: false, name: name, reason: 'vendor_unconfigured:' + name }); return; }
    if (GLOBAL[name]()) { entry.result = 'present'; resolve({ ok: true, name: name }); return; }
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.setAttribute('data-bg-vendor', name);
    s.onload = () => {
      const ok = !!GLOBAL[name]();
      entry.result = ok ? 'loaded' : 'global_missing';
      resolve(ok ? { ok: true, name: name } : { ok: false, name: name, reason: 'vendor_global_missing:' + name });
    };
    s.onerror = () => {
      entry.result = 'missing';
      try { s.remove(); } catch (_) {}
      resolve({ ok: false, name: name, reason: 'vendor_missing:' + name });
    };
    entry.at = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    try { entry.event = (window.event && window.event.type) || null; } catch (_) { entry.event = null; }
    (document.head || document.documentElement).appendChild(s);
  }).then(r => {
    if (!r.ok) loaded.delete(name);   /* a later call may try a failed file again */
    return r;
  });
  loaded.set(name, p);
  return p;
}

async function chain(list) {
  const results = [], failed = new Set();
  for (const name of list) {
    const dep = NEEDS[name];
    if (dep && failed.has(dep)) {
      results.push({ ok: false, name: name, reason: 'vendor_dependency:' + dep });
      failed.add(name);
      continue;
    }
    const r = await inject(name);
    results.push(r);
    if (!r.ok) failed.add(name);
  }
  return results;
}

function finish(results) {
  const bad = results.find(r => !r.ok);
  if (bad) return { ok: false, reason: bad.reason, loaded: results.filter(r => r.ok).map(r => r.name) };
  try {
    if (window.gsap && window.CustomEase && typeof window.gsap.registerPlugin === 'function') window.gsap.registerPlugin(window.CustomEase);
  } catch (_) {}
  return { ok: true, PIXI: window.PIXI || null, gsap: window.gsap || null, CustomEase: window.CustomEase || null };
}

/* load(names, {store, timeoutMs}) */
export function load(names, opts) {
  const store = opts && opts.store;
  const timeoutMs = (opts && opts.timeoutMs) || TIMEOUT_MS;
  if (config().vendorFx === false) {
    setFx(store, '2d');
    return Promise.resolve({ ok: false, reason: 'vendor_off' });
  }
  const list = withDeps(Array.isArray(names) ? names : ORDER);
  const run = nextTask().then(() => chain(list)).then(finish);
  let timer = null;
  const bound = new Promise(resolve => { timer = setTimeout(() => resolve({ ok: false, reason: 'vendor_timeout' }), timeoutMs); });
  return Promise.race([run, bound]).then(result => {
    clearTimeout(timer);
    setFx(store, result.ok ? 'pixi' : '2d');
    return result;
  }, () => {
    clearTimeout(timer);
    setFx(store, '2d');
    return { ok: false, reason: 'vendor_error' };
  });
}

/* preload({store}): every library, memoized until it finishes */
export function preload(opts) {
  if (inflight) return inflight;
  inflight = load(ORDER, opts).then(result => {
    if (!result.ok) inflight = null;
    return result;
  });
  return inflight;
}

export function vendorLog() { return log.map(e => Object.assign({}, e)); }
